import { Breadcrumb, HeaderToolbar } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { DeliveryReport } from "types";
import { Page } from "../Page";
import { MessageDeliveryDrawer } from "@/components/MessageDelivery/MessageDeliveryDrawer";
import { MessageDeliveryFilters } from "@/components/MessageDelivery/MessageDeliveryFilters";
import { MessageDeliveryTable } from "@/components/MessageDelivery/MessageDeliveryTable";
import { useDeliveryReports } from "@/hooks/resources/useDeliveryReports";
import { useQueryParams } from "@/hooks/useQueryParams";
import { useModal } from "@/hooks/useVisibility";
import {
    DELIVERY_REPORT_GUTTER_CLASS,
    DeliveryReportFilters,
    getDefaultDeliveryReportFilters,
    parseDeliveryReportFilters,
    serializeDeliveryReportFilters,
} from "@/models/DeliveryReport";

// A link that carries a search or status is a targeted view (e.g. a purchase's phone number), and
// imposing a trailing-month window on it would silently hide older delivery reports, making a
// message that was actually sent look like it never was. Only a completely bare landing page (no
// search, no status, no range) gets the trailing-month default.
export const getInitialFilters = (queryParams: URLSearchParams): DeliveryReportFilters => {
    const parsed = parseDeliveryReportFilters(queryParams);
    const hasExplicitFilter = !!parsed.search || parsed.statuses.length > 0 || !!parsed.from || !!parsed.to;

    if (hasExplicitFilter) {
        return parsed;
    }

    const { from, to } = getDefaultDeliveryReportFilters();

    return { ...parsed, from, to };
};

export const MessageDeliveryPage = () => {
    const history = useHistory();
    const queryParams = useQueryParams();
    const [filters, setFilters] = useState<DeliveryReportFilters>(() => getInitialFilters(queryParams));
    const [selectedReport, setSelectedReport] = useState<DeliveryReport>();
    const drawer = useModal();

    const { deliveryReports, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useDeliveryReports(filters);

    const handleFiltersChange = useMemoizedFn((value: DeliveryReportFilters) => {
        setFilters(value);
        // Keeps the view linkable, which is what lets a purchase link straight to a traveler's messages.
        history.replace({ search: serializeDeliveryReportFilters(value) });
    });

    const handleSelectReport = useMemoizedFn((report: DeliveryReport) => {
        setSelectedReport(report);
        drawer.show();
    });

    return (
        <Page className="ui-message-delivery-page" title="Message Delivery">
            <div className="flex h-screen w-full flex-col overflow-hidden">
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb>
                        <Breadcrumb.Item>Debug</Breadcrumb.Item>
                        <Breadcrumb.Item>Message Delivery</Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>
                </HeaderToolbar>

                <MessageDeliveryFilters filters={filters} onChange={handleFiltersChange} />

                <div className="flex-1 overflow-auto">
                    {/* Matches the filter row's inset so the table doesn't run flush to the page edge. */}
                    <div className={`${DELIVERY_REPORT_GUTTER_CLASS} pt-3 pb-20`}>
                        <MessageDeliveryTable
                            reports={deliveryReports}
                            isLoading={isLoading}
                            isFetchingNextPage={isFetchingNextPage}
                            hasNextPage={hasNextPage ?? false}
                            onLoadMore={fetchNextPage}
                            onSelectReport={handleSelectReport}
                        />
                    </div>
                </div>
            </div>

            <MessageDeliveryDrawer report={selectedReport} isOpen={drawer.isVisible} onClose={drawer.hide} />
        </Page>
    );
};
